import { ManagedAppPlatform } from 'enums';
import {
  MANAGED_APP_NAME_MAX_LENGTH,
  MANAGED_APP_PRESETS
} from 'constants/managed-blocking';
import {
  ManagedAppPreset,
  ManagedBlockedApp
} from 'interfaces/managed-blocking.interface';

// The same formats the backend enforces (Focus-Bear/backend#2721): reverse-DNS bundle and package
// ids, and an executable name on Windows.
const REVERSE_DNS = /^[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/;
const WINDOWS_EXECUTABLE = /^[^\\/:*?"<>|]+\.exe$/i;
const IDENTIFIER_PATTERNS: Record<ManagedAppPlatform, RegExp> = {
  [ManagedAppPlatform.MACOS]: REVERSE_DNS,
  [ManagedAppPlatform.IOS]: REVERSE_DNS,
  [ManagedAppPlatform.ANDROID]: REVERSE_DNS,
  [ManagedAppPlatform.WINDOWS]: WINDOWS_EXECUTABLE
};

export const isManagedAppIdentifierValid = (
  platform: ManagedAppPlatform,
  identifier: string
) => IDENTIFIER_PATTERNS[platform]?.test(identifier.trim()) === true;

export const isManagedBlockedAppValid = (app: ManagedBlockedApp) =>
  isManagedAppIdentifierValid(app.platform, app.identifier) &&
  app.name.trim().length > 0 &&
  app.name.trim().length <= MANAGED_APP_NAME_MAX_LENGTH;

const sameApp = (left: ManagedBlockedApp, right: ManagedBlockedApp) =>
  left.platform === right.platform &&
  left.identifier.toLowerCase() === right.identifier.toLowerCase();

export const presetBlockedApps = (preset: ManagedAppPreset) =>
  (Object.entries(preset.identifiers) as [ManagedAppPlatform, string][]).map(
    ([platform, identifier]) => ({ platform, identifier, name: preset.name })
  );

export const isPresetBlocked = (
  preset: ManagedAppPreset,
  apps: ManagedBlockedApp[]
) =>
  presetBlockedApps(preset).every((app) =>
    apps.some((item) => sameApp(item, app))
  );

/** Adds the preset's apps (skipping ones already there), or removes them all. */
export const togglePresetApps = (
  preset: ManagedAppPreset,
  apps: ManagedBlockedApp[]
): ManagedBlockedApp[] => {
  const presetApps = presetBlockedApps(preset);
  if (isPresetBlocked(preset, apps)) {
    return apps.filter((app) => !presetApps.some((item) => sameApp(item, app)));
  }
  return [
    ...apps,
    ...presetApps.filter((app) => !apps.some((item) => sameApp(item, app)))
  ];
};

export const addBlockedApp = (
  apps: ManagedBlockedApp[],
  app: ManagedBlockedApp
) => (apps.some((item) => sameApp(item, app)) ? apps : [...apps, app]);

/** Selected apps grouped by display name, so one app covering four platforms reads as one row. */
export const groupBlockedApps = (apps: ManagedBlockedApp[]) =>
  apps.reduce<{ name: string; apps: ManagedBlockedApp[] }[]>((groups, app) => {
    const group = groups.find((item) => item.name === app.name);
    if (group === undefined) groups.push({ name: app.name, apps: [app] });
    else group.apps.push(app);
    return groups;
  }, []);

/** A display name for an identifier seen in attempt history, from the presets when we know it. */
export const managedAppNameFor = (identifier: string): string | undefined =>
  MANAGED_APP_PRESETS.find((preset) =>
    Object.values(preset.identifiers).some(
      (value) => value?.toLowerCase() === identifier.toLowerCase()
    )
  )?.name;

/** One website per line (pasted from a spreadsheet or document), trimmed and de-duplicated. */
export const parseWebsiteLines = (text: string): string[] =>
  Array.from(
    new Set(
      text
        .split(/[\r\n,]+/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
    )
  );
